import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import UploadField from "./UploadField";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { name: "", category: "", price: "", fitment: "", desc: "", image: "", stock: "" };
const inputCls = "w-full bg-[#0D1117] border border-[#263342] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-crimson transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2";

function stockBadge(stock) {
  if (stock === null || stock === undefined) return null;
  if (stock <= 0) return <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-red-400 border border-red-400/40 bg-red-400/10 px-2 py-1">Sold out</span>;
  if (stock <= 2) return <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-crimson border border-crimson/50 bg-crimson/10 px-2 py-1">Only {stock} left</span>;
  return <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-400 border border-emerald-400/40 bg-emerald-400/10 px-2 py-1">{stock} in stock</span>;
}

export default function PartsManager() {
  const [parts, setParts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/parts");
      setParts(data);
    } catch (err) {
      toast.error("Could not load parts", { description: formatApiError(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, category: p.category, price: p.price,
      fitment: p.fitment || "", desc: p.desc || "", image: p.image || "",
      stock: p.stock === null || p.stock === undefined ? "" : String(p.stock),
    });
    setDialogOpen(true);
  };
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name, category: form.category, price: form.price,
        fitment: form.fitment, desc: form.desc, image: form.image,
        stock: form.stock === "" ? null : Math.max(0, parseInt(form.stock, 10) || 0),
      };
      if (editing) {
        await api.put(`/parts/${editing.id}`, payload);
        toast.success("Part updated");
      } else {
        await api.post("/parts", payload);
        toast.success("Part added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/parts/${id}`);
      toast.success("Part removed");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    }
  };

  const adjustStock = async (p, delta) => {
    const current = p.stock ?? 0;
    const next = Math.max(0, current + delta);
    try {
      await api.put(`/parts/${p.id}`, {
        name: p.name, category: p.category, price: p.price,
        fitment: p.fitment || "", desc: p.desc || "", image: p.image || "", stock: next,
      });
      setParts((list) => list.map((x) => (x.id === p.id ? { ...x, stock: next } : x)));
    } catch (err) {
      toast.error("Stock update failed", { description: formatApiError(err) });
    }
  };

  return (
    <div className="mt-12" data-testid="parts-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold uppercase tracking-tight text-2xl text-slate-100">Parts &amp; Packages</h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">Shown on the site under Services — low stock shows an urgency badge</p>
        </div>
        <button
          data-testid="part-add-button"
          onClick={openAdd}
          className="flex items-center gap-2 bg-crimson text-white font-display font-semibold uppercase tracking-widest text-sm px-5 py-2.5 hover:-translate-y-0.5 transition-transform"
        >
          <Plus size={16} /> Add Part
        </button>
      </div>

      <div className="bg-[#0D1117] border border-[#263342] divide-y divide-[#263342]/60" data-testid="parts-manager-list">
        {parts.map((p, i) => (
          <div key={p.id} className="flex flex-wrap items-center gap-3 px-5 py-4" data-testid={`part-admin-row-${i}`}>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 border border-[#263342] px-2 py-1">{p.category}</span>
            <span className="flex-1 min-w-[160px] font-semibold text-slate-100 text-sm">{p.name}</span>
            <span className="font-mono text-xs text-crimson" data-testid={`part-admin-price-${i}`}>{p.price}</span>
            <span data-testid={`part-admin-stock-${i}`}>{stockBadge(p.stock)}</span>
            <span className="flex items-center gap-1">
              <button
                data-testid={`part-stock-minus-${i}`}
                onClick={() => adjustStock(p, -1)}
                disabled={p.stock === null || p.stock === undefined}
                className="w-8 h-8 border border-[#263342] text-slate-300 hover:border-crimson hover:text-crimson transition-colors disabled:opacity-30 font-mono"
                aria-label="Decrease stock"
              >−</button>
              <button
                data-testid={`part-stock-plus-${i}`}
                onClick={() => adjustStock(p, 1)}
                disabled={p.stock === null || p.stock === undefined}
                className="w-8 h-8 border border-[#263342] text-slate-300 hover:border-crimson hover:text-crimson transition-colors disabled:opacity-30 font-mono"
                aria-label="Increase stock"
              >+</button>
            </span>
            <button
              data-testid={`part-edit-${i}`}
              onClick={() => openEdit(p)}
              className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-3 py-2 text-slate-300 hover:border-crimson hover:text-crimson transition-colors"
            >
              <Pencil size={12} /> Edit
            </button>
            {confirmDelete === p.id ? (
              <button
                data-testid={`part-delete-confirm-${i}`}
                onClick={() => remove(p.id)}
                className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] border border-red-500 bg-red-500/10 px-3 py-2 text-red-400"
              >
                <Trash2 size={12} /> Confirm?
              </button>
            ) : (
              <button
                data-testid={`part-delete-${i}`}
                onClick={() => setConfirmDelete(p.id)}
                className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-3 py-2 text-slate-400 hover:border-red-500 hover:text-red-400 transition-colors"
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
        {parts.length === 0 && (
          <p className="font-mono text-sm text-slate-500 text-center py-8" data-testid="parts-empty">No parts yet. Add your first one.</p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-panel border-[#263342] text-slate-100 max-w-lg" data-testid="part-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold uppercase tracking-tight text-2xl">
              {editing ? "Edit Part" : "Add Part"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="mt-2 space-y-4" data-testid="part-form">
            <div>
              <label className={labelCls}>Name *</label>
              <input required className={inputCls} data-testid="part-form-name" value={form.name} onChange={set("name")} placeholder="KW V3 Coilover Kit" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Category *</label>
                <input required className={inputCls} data-testid="part-form-category" value={form.category} onChange={set("category")} placeholder="Suspension" />
              </div>
              <div>
                <label className={labelCls}>Price *</label>
                <input required className={inputCls} data-testid="part-form-price" value={form.price} onChange={set("price")} placeholder="$2,650" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Fitment</label>
                <input className={inputCls} data-testid="part-form-fitment" value={form.fitment} onChange={set("fitment")} placeholder="BMW M / Audi RS" />
              </div>
              <div>
                <label className={labelCls}>Stock count (blank = not tracked)</label>
                <input type="number" min="0" className={inputCls} data-testid="part-form-stock" value={form.stock} onChange={set("stock")} placeholder="e.g. 3" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea rows={3} className={inputCls} data-testid="part-form-desc" value={form.desc} onChange={set("desc")} />
            </div>
            <UploadField
              label="Photo (optional)"
              testid="part-form-image"
              value={form.image}
              onChange={(url) => setForm((f) => ({ ...f, image: url }))}
            />
            <button type="submit" data-testid="part-form-save" disabled={saving} className="w-full bg-crimson text-white font-display font-semibold uppercase tracking-widest py-3.5 disabled:opacity-60">
              {saving ? "Saving..." : editing ? "Save Changes" : "Add Part"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
